import * as React from "react";
import { useEffect } from "react";
import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import { Loader } from "../Loader";
import { TranslateText } from "../TranslateText";
import { DateFilter } from '../DateFilter';
import { useIntl } from "react-intl";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const chartMapping = {
  "Cooks": "KC0095",
  "Trays": "KC1897",
  "Cleanings": "KC0407"
};
const yAxisConfig = {
  default: 0,
  fontSize: 12,
  fontWeight: "400",
  strokeOpacity: 0.5,
  cellStartLocation: -0.1,
  cellEndLocation: 1.1,
  minGridDistance: 10,
  sOpacity: 1,
  strokeWidth: 0.7
};
const LineChart = ({
  data,
  dateFilterValue,
  categoryEnds,
  categoryStarts,
  setMaxPrecision,
  average
}) => {
  const randomID = (Math.random() + 1).toString(36).substring(7);
  const intl = useIntl();
  const bodyStyles = window.getComputedStyle(document.body);
  const defaultColor = bodyStyles.getPropertyValue("--kc-txt-color");
  useEffect(() => {
    // Themes begin
    am4core.useTheme(am4themes_animated);
    // Themes end

    let chart = am4core.create(randomID, am4charts.XYChart);
    renderChartData(chart, dateFilterValue, average, data, categoryStarts, categoryEnds);

    // Clean up when the component unmounts
    return () => {
      chart.dispose();
    };
  }, []);
  const renderChartData = (chart, type, average, data, categoryStarts, categoryEnds) => {
    let response = data;
    if (response && response.length > 0) {
      chart.seriesContainer.zIndex = -1;
      chart.data = response;
      chart.responsive.enabled = true;

      // Create axes
      let categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
      categoryAxis.dataFields.category = "x";
      categoryAxis.renderer.grid.template.location = 0;
      categoryAxis.renderer.minGridDistance = type === "Year" || type === "Quarter" ? 60 : 10;
      categoryAxis.renderer.labels.template.fill = defaultColor;
      // categoryAxis.renderer.labels.template.fill = am4core.color("#FFF");
      categoryAxis.renderer.labels.template.fontSize = 10;
      categoryAxis.renderer.inside = false;
      categoryAxis.renderer.labels.template.disabled = false;
      categoryAxis.renderer.grid.template.strokeOpacity = 0;
      categoryAxis.renderer.axisFills.template.disabled = false;
      categoryAxis.renderer.axisFills.template.fill = am4core.color("#D2D68D");
      // categoryAxis.renderer.axisFills.template.fill = defaultColor;
      categoryAxis.renderer.axisFills.template.fillOpacity = 0.2;
      let valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
      valueAxis.renderer.grid.template.location = 0;
      valueAxis.renderer.minGridDistance = 100;
      // Enable .total and .totalPercent placeholders for stacked series
      valueAxis.calculateTotals = true;
      valueAxis.renderer.inside = false;
      valueAxis.renderer.labels.template.disabled = false;
      valueAxis.min = 0;
      valueAxis.renderer.labels.template.fill = defaultColor;
      valueAxis.integersOnly = true;
      if (setMaxPrecision) {
        valueAxis.maxPrecision = 0;
      }
      valueAxis.renderer.labels.template.fill = defaultColor;
      valueAxis.renderer.labels.template.fontSize = 10;
      valueAxis.renderer.grid.template.strokeOpacity = 1;
      valueAxis.renderer.grid.template.stroke = defaultColor;
      // valueAxis.renderer.grid.template.stroke = am4core.color("#FFF");
      valueAxis.renderer.grid.template.strokeWidth = 0.3;
      let averageRounded = Math.round(average ? average : 0);
      if (averageRounded > 0) {
        var range = valueAxis.axisRanges.create();
        range.value = averageRounded;
        range.grid.stroke.above = true;
        range.grid.above = true;
        range.grid.stroke = defaultColor;
        range.grid.strokeWidth = 1.5;
        range.grid.strokeOpacity = 1;
      }

      // Setting the Series Name based on the unit brand
      let seriesName = "Cooks";
      // Create series
      let series = chart.series.push(new am4charts.ColumnSeries());
      series.dataFields.valueY = "y";
      series.dataFields.categoryX = "x";
      series.dataFields.tootltipY = "tooltipNum";
      series.name = seriesName;
      series.columns.template.width = am4core.percent(93);
      series.columns.template.tooltipText = " " + seriesName + " : [bold]{valueY}[/]";
      series.columns.template.properties.fillOpacity = 1;
      series.columns.template.properties.fill = am4core.color("#006b94");

      // tooltip - to display the cleaning name
      series.tooltip.label.adapter.add("textOutput", function (_, target) {
        let seriesNameTrans = seriesName != '' ? intl.formatMessage({
          id: chartMapping[seriesName]
        }) : '';
        if (target.dataItem.dataContext != undefined) {
          if (target.dataItem.dataContext.y !== 0) {
            return " " + seriesNameTrans + " : [bold]" + target.dataItem.dataContext.y + "[/]";
          } else {
            return "";
          }
        } else {
          return "";
        }
      });

      // Series Tooltip Test and bg color
      series.tooltip.getFillFromObject = false;
      // series.tooltip.background.fill = am4core.color("#FFFFFF");
      series.tooltip.background.fill = defaultColor;
      series.tooltip.label.fill = am4core.color("#000000");
      categoryAxis.fillRule = function (dataItem) {
        if (dataItem.alpha !== undefined && dataItem.alpha === 1) {
          dataItem.axisFill.visible = true;
        } else {
          dataItem.axisFill.visible = false;
        }
      };

      // Create category axis range
      var categoryAxis_range = categoryAxis.axisRanges.create();
      categoryAxis_range.category = categoryStarts;
      categoryAxis_range.endCategory = categoryEnds;
      categoryAxis_range.axisFill.fill = am4core.color("#396478");
      categoryAxis_range.axisFill.fillOpacity = 0.3;
      categoryAxis_range.label.disabled = true;

      // Create a hover state
      var hoverState = series.columns.template.states.create("hover");
      hoverState.properties.fill = am4core.color("#00a4f2");
      let columnTemplate = series.columns.template;
      columnTemplate.strokeWidth = 0;
      columnTemplate.strokeOpacity = 0;
      categoryAxis.renderer.grid.template.location = 0;
    } else {
      if (chart) {
        chart.data = [];
      }
    }
  };
  return /*#__PURE__*/_jsx("div", {
    className: "kc-ui-chart",
    id: randomID
  });
};
const _BarChartTarget = props => {
  const {
    title,
    width,
    dateFilterTitle,
    dateFilter,
    dateFilterOptions,
    dateFilterValue,
    onTriggerDateFilterChange,
    dateFilterDateFormat,
    disabled,
    loader,
    titleMessage,
    subTitleMessage,
    data,
    noValueText,
    strokeColor,
    categoryStarts,
    categoryEnds,
    setMaxPrecision,
    average
  } = props;
  const [chartValue, setChartValue] = React.useState(data);
  const [loading, setLoading] = React.useState(false);
  React.useEffect(() => {
    setChartValue(data);
    setLoading(loader);
  }, [data, loader]);
  return /*#__PURE__*/_jsx(React.Fragment, {
    children: /*#__PURE__*/_jsx("div", {
      className: `kc-ui-colm kc-ui-bar-chart-target ${disabled && "kc-ui-disabled "}  ${loader && "kc-ui-loader-overlay"}`,
      style: {
        width: `${width}`
      },
      children: /*#__PURE__*/_jsxs("div", {
        className: "kc-ui-colm-div",
        children: [title ? /*#__PURE__*/_jsx("h4", {
          children: /*#__PURE__*/_jsx(TranslateText, {
            id: title
          })
        }) : null, /*#__PURE__*/_jsx("div", {
          className: "kc-ui-datefilter-div",
          children: dateFilter && /*#__PURE__*/_jsx(DateFilter, {
            title: dateFilterTitle,
            options: dateFilterOptions,
            value: dateFilterValue,
            onTriggerChange: onTriggerDateFilterChange,
            dateFormat: dateFilterDateFormat
          })
        }), loader ? /*#__PURE__*/_jsx(Loader, {
          loader: loader
        }) : /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsxs("div", {
            className: "kc-ui-bar-chart-text-wrapper",
            children: [/*#__PURE__*/_jsx("div", {
              className: "kc-ui-bar-chart-value-title",
              children: /*#__PURE__*/_jsx("p", {
                children: /*#__PURE__*/_jsx(TranslateText, {
                  id: titleMessage
                })
              })
            }), /*#__PURE__*/_jsxs("div", {
              className: "kc-ui-bar-chart-value",
              children: [" ", /*#__PURE__*/_jsx("p", {
                children: subTitleMessage || ""
              })]
            })]
          }), /*#__PURE__*/_jsx("div", {
            className: "",
            children: loading ? /*#__PURE__*/_jsx(Loader, {
              loader: loading
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [chartValue.length ? /*#__PURE__*/_jsx("div", {
                className: "kc-ui-bar-chart-div",
                children: /*#__PURE__*/_jsx(LineChart, {
                  data: chartValue,
                  subTitleMessage: subTitleMessage,
                  strokeColor: strokeColor,
                  dateFilterValue: dateFilterValue,
                  categoryStarts: categoryStarts,
                  categoryEnds: categoryEnds,
                  setMaxPrecision: setMaxPrecision,
                  average: average
                })
              }) : null, !chartValue || !chartValue.length || typeof chartValue !== "object" && noValueText ? /*#__PURE__*/_jsx("p", {
                className: "kc-ui-align-no-result kc-ui-align-center",
                children: /*#__PURE__*/_jsx(TranslateText, {
                  id: noValueText
                })
              }) : null]
            })
          })]
        })]
      })
    })
  });
};
_BarChartTarget.defaultProps = {
  title: "",
  width: "100%",
  loader: false,
  dateFilterTitle: '',
  dateFilter: false,
  dateFilterOptions: [],
  onTriggerDateFilterChange: () => {},
  dateFilterValue: {},
  dateFilterDateFormat: "MM/dd/yyyy",
  noValueText: "",
  disabled: false,
  data: [],
  titleMessage: "",
  subTitleMessage: "",
  strokeColor: "",
  setMaxPrecision: false,
  average: 0
};
export const BarChartTargetWidget = /*#__PURE__*/React.memo(_BarChartTarget);